// src/data/propertyTools.ts

import {
  CalculatorIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  BellIcon,
  ClipboardDocumentListIcon,
  WrenchScrewdriverIcon,
  HomeIcon
} from '@heroicons/react/24/outline';
import React from 'react';

export interface PropertyTool {
  name: string;
  description: string;
  icon: React.ForwardRefExoticComponent<
    Omit<React.SVGProps<SVGSVGElement>, 'ref'> & {
      title?: string;
      titleId?: string;
    } & React.RefAttributes<SVGSVGElement>
  >;
  path: string;
  featured?: boolean;
}

export const propertyTools: PropertyTool[] = [
  {
    name: 'Price Trends by County',
    description: 'Explore Irish market price trends by county and area.',
    icon: ChartBarIcon,
    path: '/prices/discover'
  },
  {
    name: 'Landlord Toolkit',
    description:
      'A comprehensive toolkit for landlords to manage their properties.',
    icon: WrenchScrewdriverIcon,
    path: '/landlord-toolkit'
  },
  {
    name: 'Home Valuation Tool',
    description: 'Get an instant estimated value for your property.',
    icon: HomeIcon,
    path: '/home-valuation'
  },
  {
    name: 'Rent Tracker',
    description:
      'Track rent payments, send reminders, and manage tenant communications.',
    icon: ClipboardDocumentListIcon,
    path: '/property-tools/rent-tracker'
  },
  {
    name: 'Mortgage Calculator',
    description:
      'Estimate your monthly mortgage payments with our easy-to-use calculator.',
    icon: CalculatorIcon,
    path: '/property-tools/mortgage-calculator'
  },
  {
    name: 'Affordability Calculator',
    description:
      'Determine how much property you can afford based on your income and expenses.',
    icon: CurrencyDollarIcon,
    path: '/property-tools/affordability-calculator'
  },
  {
    name: 'Cost of Living Ireland',
    description:
      'Interactive county calculators for rent, transport, childcare and monthly lifestyle budgets.',
    icon: CurrencyDollarIcon,
    path: '/cost-of-living'
  },
  {
    name: 'Irish City Comparisons',
    description:
      'Compare Dublin vs Cork, Galway vs Limerick and more for rent, pricing and lifestyle fit.',
    icon: ChartBarIcon,
    path: '/city-comparisons'
  },
  {
    name: 'Dublin vs Commuter Towns',
    description:
      'See your real monthly gain after commute costs when comparing Dublin to commuter towns.',
    icon: ChartBarIcon,
    path: '/compare/renting-in-dublin-vs-commuter-towns'
  },
  {
    name: 'Eviction Rules 2026',
    description:
      'County-by-county 2026 eviction rights with notice periods, landlord tiers, and legal aid links.',
    icon: ClipboardDocumentListIcon,
    path: '/rights'
  },
  {
    name: 'Self-Build Cost 2026',
    description:
      'Estimate the cost to build a 2-bed or 3-bed house in every county using the SCSI benchmark seed.',
    icon: HomeIcon,
    path: '/self-build'
  },
  {
    name: 'New Build vs Old Build',
    description:
      'County-by-county decision matrix comparing grants, BER energy costs, and 5-year ownership costs.',
    icon: ChartBarIcon,
    path: '/new-vs-old'
  },
  {
    name: 'Salary After Tax Ireland',
    description:
      'See net pay after PAYE, USC and PRSI, then jump to rent or buy budgets.',
    icon: CurrencyDollarIcon,
    path: '/salary-after-tax'
  },
  {
    name: 'Good Salary by County',
    description:
      'Check if your salary is good in Dublin, Cork, Galway and every Irish county with rent realism.',
    icon: CurrencyDollarIcon,
    path: '/salary'
  },
  {
    name: 'Rent Affordability by County',
    description:
      'Find your max rent on each salary using Ireland 2026 tax rules, 35% net guidance, and county rent realism.',
    icon: CurrencyDollarIcon,
    path: '/affordability'
  },
  {
    name: 'RPZ Notice Generator',
    description:
      'Check rent increase compliance and draft an RTB-ready rent review notice by county.',
    icon: CalculatorIcon,
    path: '/tools/rent-increase-notice-generator'
  },
  {
    name: 'Schools Around Neighborhoods',
    description:
      'Explore schools around Irish neighborhoods, then jump straight to rent or buy listings.',
    icon: HomeIcon,
    path: '/schools-around'
  },

  {
    name: 'Rent Review Calculator',
    description: 'Calculate allowable rent increases using HICP and RPZ rules.',
    icon: CalculatorIcon,
    path: '/property-tools/rent-review-calculator'
  },

  {
    name: 'Rental Yield Calculator',
    description:
      'Calculate the potential rental yield of your investment property.',
    icon: ChartBarIcon,
    path: '/property-tools/rental-yield-calculator'
  },
  {
    name: 'Property Alerts',
    description:
      'Stay updated with alerts for new listings and market changes.',
    icon: BellIcon,
    path: '/property-tools/property-alerts'
  }
];
